import { useEffect, useRef, useState } from 'react';

// hooks
import useWebsocketConnection from './useWebsocketConnection';

// Config
import { PayloadActions, CallbackActions, ROBOT_STATUS_DATA_KEYS } from 'Config/websocketConstants';

// Utils
import { validateRobotStatusData } from 'Utils/websocketUtils';

// A robot with no message for this long is treated as offline
const STALE_STATUS_MS = 60 * 1000;
const STALE_SWEEP_INTERVAL_MS = 60 * 1000;

/**
 * Robot live status over the shared websocket (fed from the MQTT stream).
 * Returns the latest status payloads (ROBOT_STATUS_DATA_KEYS shape) keyed by
 * DB robot id; a robot missing from the result is offline.
 *
 * @param {string} wsClientName - Key of WebsocketClients.
 * @param {object} airIdToRobotId - {air_id: robot_id} of the robots to watch.
 */
const useRobotLiveStatus = (wsClientName, airIdToRobotId) => {
  const [robotLiveStatus, setRobotLiveStatus] = useState({});
  const airIdToRobotIdRef = useRef(airIdToRobotId); // Let the ws callback see the latest map
  const receivedAtByRobotId = useRef({});

  useEffect(() => {
    airIdToRobotIdRef.current = airIdToRobotId;
  }, [airIdToRobotId]);

  const actionToCallbackObj = useRef({
    [CallbackActions.ROBOT_STATUS_UPDATE]: (statusData) => {
      if (!validateRobotStatusData(statusData)) {
        return;
      }
      const robotId = airIdToRobotIdRef.current?.[statusData[ROBOT_STATUS_DATA_KEYS.AIR_ID]];
      if (robotId === undefined) {
        return;
      }
      receivedAtByRobotId.current[robotId] = Date.now();
      setRobotLiveStatus(prev => ({ ...prev, [robotId]: statusData }));
    },
  });

  const { sendPayload } = useWebsocketConnection(wsClientName, actionToCallbackObj.current);

  useEffect(() => { // The manager opens the socket and resubscribes after reconnects
    const airIdsString = Object.keys(airIdToRobotId ?? {}).join(',');
    if (airIdsString.length > 0) {
      sendPayload({
        "action": PayloadActions.CONNECT_AIR_ID_GROUPS,
        "data": { "air_ids": airIdsString }
      });
    }
  }, [airIdToRobotId]);

  useEffect(() => { // Drop robots that stopped publishing so they render as offline
    const sweepIntervalId = setInterval(() => {
      const now = Date.now();
      const staleRobotIds = Object.keys(receivedAtByRobotId.current).filter(
        (robotId) => now - receivedAtByRobotId.current[robotId] >= STALE_STATUS_MS
      );
      if (staleRobotIds.length === 0) {
        return;
      }
      staleRobotIds.forEach((robotId) => { delete receivedAtByRobotId.current[robotId]; });
      setRobotLiveStatus(prev => {
        const newStatus = { ...prev };
        staleRobotIds.forEach((robotId) => { delete newStatus[robotId]; });
        return newStatus;
      });
    }, STALE_SWEEP_INTERVAL_MS);

    return () => clearInterval(sweepIntervalId);
  }, []);

  return robotLiveStatus;
};

export default useRobotLiveStatus;
